import { Gift, Pencil, ShoppingBag, Trash2, Undo2 } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { api, call, type Schemas } from '../../api/client';
import { useAction, useRewards } from '../../api/queries';
import { useHousehold } from '../../api/session';
import { Badge, Button, Card, ErrorText, Field, Input, Modal } from '../../components/ui';
import { timeAgo } from '../../lib/format';

type Points = Schemas['PointsSummaryResponse'];
type Entry = Schemas['PointEntriesResponse']['items'][number];
type Reward = Schemas['RewardResponse'];

const idempotency = () => ({ 'Idempotency-Key': crypto.randomUUID() });

/** Balance front and center, with what was earned and spent (ADR-0020). */
export function PointsOverview({ points, onSpend }: { points: Points | undefined; onSpend(): void }) {
  if (!points) return null;
  return (
    <Card className="mb-6 flex flex-wrap items-center gap-x-8 gap-y-3">
      <div>
        <p className="text-xs text-[var(--muted)]">Points to spend</p>
        <p className="text-3xl font-semibold">⭐ {points.balance}</p>
      </div>
      <dl className="flex gap-6 text-sm">
        <div><dt className="text-xs text-[var(--muted)]">Earned</dt><dd>{points.earned}</dd></div>
        <div><dt className="text-xs text-[var(--muted)]">Spent</dt><dd>{points.spent}</dd></div>
        <div><dt className="text-xs text-[var(--muted)]">This week</dt><dd>+{points.thisWeek}</dd></div>
      </dl>
      <Button className="ml-auto" onClick={onSpend} disabled={points.balance < 1}>
        <ShoppingBag size={16} /> Spend points…
      </Button>
    </Card>
  );
}

/** What the child is saving toward: every reward with progress against the current balance. */
export function SavingToward({ balance, onSpend, onManage }: { balance: number; onSpend(reward: Reward): void; onManage(): void }) {
  const rewards = useRewards().data ?? [];
  return (
    <section className="mb-6">
      <h2 className="mb-2 flex items-center justify-between text-sm font-medium text-[var(--muted)]">
        Rewards
        <Button size="sm" variant="ghost" onClick={onManage}><Pencil size={14} /> Edit rewards</Button>
      </h2>
      {rewards.length ? (
        <div className="grid gap-2 sm:grid-cols-2">
          {rewards.map((r) => {
            const pct = Math.min(100, Math.round((balance / r.points) * 100));
            const affordable = balance >= r.points;
            return (
              <Card key={r.id} className="flex items-center gap-3">
                <Gift size={18} className="shrink-0 text-[var(--accent)]" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{r.title}</p>
                  <div className="mt-1 flex items-center gap-2 text-xs text-[var(--muted)]">
                    <span className="h-1.5 w-24 overflow-hidden rounded-full bg-[var(--surface-2)]" aria-hidden>
                      <span className="block h-full rounded-full bg-[var(--accent)]" style={{ width: `${pct}%` }} />
                    </span>
                    {affordable ? `${r.points} points` : `${Math.min(balance, r.points)} / ${r.points} · ${r.points - balance} to go`}
                  </div>
                </div>
                {affordable ? <Button size="sm" variant="secondary" onClick={() => onSpend(r)}>Spend</Button> : null}
              </Card>
            );
          })}
        </div>
      ) : (
        <Card className="text-sm text-[var(--muted)]">
          No rewards yet. <button type="button" className="text-[var(--accent)] underline" onClick={onManage}>Add some</button>, like “$5 allowance” or a toy they're saving for.
        </Card>
      )}
    </section>
  );
}

export function SpendDialog({ childId, name, balance, initial, onClose }: { childId: string; name: string; balance: number; initial?: Reward; onClose(): void }) {
  const { familyId } = useHousehold();
  const rewards = useRewards().data ?? [];
  const [mode, setMode] = useState<'reward' | 'other'>(initial || rewards.length ? 'reward' : 'other');
  const [rewardId, setRewardId] = useState<string | undefined>(initial?.id);
  const spend = useAction(
    (body: { rewardId?: string; points?: number; description?: string }) =>
      call(api.POST('/api/v1/families/{familyId}/children/{childId}/points-redemptions', { params: { path: { familyId, childId }, header: idempotency() }, body })),
    { success: 'Points spent' },
  );
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const body = mode === 'reward' ? { rewardId } : { points: Number(f.get('points')), description: String(f.get('description')) };
    spend.mutate(body, { onSuccess: onClose });
  };
  return (
    <Modal open onOpenChange={onClose} title={`${name} spends points`}>
      <form className="space-y-4" onSubmit={submit}>
        <p className="text-sm">⭐ <strong>{balance}</strong> points to spend</p>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant={mode === 'reward' ? 'primary' : 'secondary'} onClick={() => setMode('reward')} disabled={!rewards.length}>A reward</Button>
          <Button type="button" size="sm" variant={mode === 'other' ? 'primary' : 'secondary'} onClick={() => setMode('other')}>Something else</Button>
        </div>
        {mode === 'reward' ? (
          <fieldset className="space-y-1.5">
            <legend className="sr-only">Reward</legend>
            {rewards.map((r) => (
              <label key={r.id} className={`flex items-center gap-2 rounded-lg border border-[var(--border)] px-3 py-2 text-sm ${r.points > balance ? 'opacity-50' : ''}`}>
                <input type="radio" name="reward" checked={rewardId === r.id} disabled={r.points > balance} onChange={() => setRewardId(r.id)} />
                <span className="flex-1">{r.title}</span>
                <span className="text-xs text-[var(--muted)]">{r.points > balance ? `${r.points - balance} more needed` : `${r.points} points`}</span>
              </label>
            ))}
          </fieldset>
        ) : (
          <>
            <Field label="Points">{(id) => <Input id={id} name="points" type="number" min={1} max={balance} required />}</Field>
            <Field label="Spent on" hint="Kept in the points history.">{(id) => <Input id={id} name="description" required maxLength={300} placeholder="Movie night snacks" />}</Field>
          </>
        )}
        <ErrorText error={spend.error} />
        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          <Button type="submit" busy={spend.isPending} disabled={mode === 'reward' && !rewardId}>Spend</Button>
        </div>
      </form>
    </Modal>
  );
}

export function ManageRewardsDialog({ onClose }: { onClose(): void }) {
  const { familyId } = useHousehold();
  const rewards = useRewards().data ?? [];
  const [editing, setEditing] = useState<Reward | null>(null);
  const save = useAction((body: { title: string; points: number }) =>
    editing
      ? call(api.PATCH('/api/v1/families/{familyId}/rewards/{rewardId}', { params: { path: { familyId, rewardId: editing.id } }, body }))
      : call(api.POST('/api/v1/families/{familyId}/rewards', { params: { path: { familyId }, header: idempotency() }, body })),
  );
  const remove = useAction((rewardId: string) => call(api.POST('/api/v1/families/{familyId}/rewards/{rewardId}/archive', { params: { path: { familyId, rewardId } } })));
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const f = new FormData(form);
    const submitted = { title: String(f.get('title')), points: Number(f.get('points')) };
    save.mutate(submitted, {
      onSuccess: () => {
        setEditing(null);
        // Runs after the list refreshed: only clear the form if it still holds what was saved (not the next one being typed).
        const now = new FormData(form);
        if (String(now.get('title')) === submitted.title && Number(now.get('points')) === submitted.points) form.reset();
      },
    });
  };
  return (
    <Modal open onOpenChange={onClose} title="Rewards">
      <p className="mb-3 text-sm text-[var(--muted)]">What the children can spend points on. Changing a reward never changes past spends.</p>
      <ul className="mb-4 divide-y divide-[var(--border)] rounded-xl border border-[var(--border)]">
        {rewards.map((r) => (
          <li key={r.id} className="flex items-center gap-2 px-3 py-2 text-sm">
            <span className="flex-1">{r.title}</span>
            <Badge>{r.points} pts</Badge>
            <Button size="sm" variant="ghost" aria-label={`Edit ${r.title}`} onClick={() => setEditing(r)}><Pencil size={14} /></Button>
            <Button size="sm" variant="ghost" aria-label={`Remove ${r.title}`} busy={remove.isPending} onClick={() => remove.mutate(r.id)}><Trash2 size={14} /></Button>
          </li>
        ))}
        {!rewards.length ? <li className="px-3 py-2 text-sm text-[var(--muted)]">None yet.</li> : null}
      </ul>
      <form key={editing?.id ?? 'new'} className="flex flex-wrap items-end gap-2" onSubmit={submit}>
        <div className="min-w-40 flex-1"><Field label={editing ? 'Reward' : 'New reward'}>{(id) => <Input id={id} name="title" required maxLength={100} defaultValue={editing?.title} placeholder="$5 allowance" />}</Field></div>
        <div className="w-28"><Field label="Points">{(id) => <Input id={id} name="points" type="number" min={1} max={100000} required defaultValue={editing?.points} />}</Field></div>
        <Button type="submit" busy={save.isPending}>{editing ? 'Save' : 'Add'}</Button>
        {editing ? <Button type="button" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button> : null}
      </form>
      <ErrorText error={save.error} />
    </Modal>
  );
}

/** One line of the points history; spends say what they were for and can be undone. */
export function PointsHistoryRow({ entry, childId }: { entry: Entry; childId: string }) {
  const { familyId } = useHousehold();
  const undo = useAction(
    () => call(api.POST('/api/v1/families/{familyId}/children/{childId}/points-redemptions/{entryId}/undo', { params: { path: { familyId, childId, entryId: entry.id } }, body: {} })),
    { success: 'Spend undone; points returned' },
  );
  const label =
    entry.kind === 'REDEMPTION' ? `Spent on ${entry.reason ?? 'a reward'}`
    : entry.kind === 'REDEMPTION_REVERSAL' ? (entry.reason ?? 'Spend undone')
    : (entry.taskTitle ?? entry.reason ?? (entry.kind === 'MANUAL_ADJUSTMENT' ? 'Adjustment' : 'Points')) + (entry.kind === 'TASK_COMPLETION_REVERSAL' ? ' (reopened)' : '');
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 text-sm">
      {entry.kind === 'REDEMPTION' ? <ShoppingBag size={15} className="shrink-0 text-[var(--muted)]" /> : null}
      <span className="flex-1">{label}</span>
      {entry.canUndo ? (
        <Button size="sm" variant="ghost" busy={undo.isPending} onClick={() => { if (confirm(`Undo “${entry.reason}”? The ${-entry.points} points go back.`)) undo.mutate(undefined); }}>
          <Undo2 size={14} /> Undo
        </Button>
      ) : null}
      <Badge tone={entry.points > 0 ? 'accent' : entry.kind === 'REDEMPTION' ? 'info' : 'danger'}>{entry.points > 0 ? '+' : ''}{entry.points}</Badge>
      <span className="w-20 text-right text-xs text-[var(--muted)]">{timeAgo(entry.createdAt)}</span>
    </div>
  );
}
